import {
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
  User,
  signInWithEmailAndPassword as firebaseSignInWithEmailAndPassword,
  createUserWithEmailAndPassword as firebaseCreateUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth"
import { doc, setDoc, getDoc } from "firebase/firestore"
import { auth, db } from "../firebase/config"

// Hardcoded admin emails
const ADMIN_EMAILS = ['admin@gmail.com', 'adminanmol@gmail.com'];

// Function to check if user is admin
export const isAdminUser = (email: string): boolean => {
  const isAdmin = ADMIN_EMAILS.includes(email);
  console.log('🔍 isAdminUser check:', { email, isAdmin, adminEmails: ADMIN_EMAILS });
  return isAdmin;
};

const googleProvider = new GoogleAuthProvider()

export const signInWithGoogle = async () => {
  try {
    console.log('🔍 Starting Google sign in...');
    const result = await signInWithPopup(auth, googleProvider)
    const user = result.user
    console.log('🔍 Google sign in successful:', { email: user.email, uid: user.uid });

    // Check if this is a new user
    const userDoc = await getDoc(doc(db, "users", user.uid))
    
    if (!userDoc.exists()) {
      console.log('🔍 Creating new user document for Google sign in');
      // This is a new user, create their profile
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        name: user.displayName || user.email?.split('@')[0] || '',
        photoURL: user.photoURL,
        role: "user", // Default role
        createdAt: new Date().toISOString(),
      })
    } else {
      console.log('🔍 Existing user document found:', userDoc.data());
    }

    return user
  } catch (error) {
    console.error('🔍 Google sign in error:', error);
    const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred"
    throw new Error(errorMessage)
  }
}

export const signInWithEmailAndPassword = async (email: string, password: string, options?: { skipEmailCheck?: boolean }) => {
  try {
    console.log('🔍 Starting email/password sign in:', { email, skipEmailCheck: options?.skipEmailCheck });
    const result = await firebaseSignInWithEmailAndPassword(auth, email, password)
    console.log('🔍 Firebase auth successful:', { email: result.user.email, uid: result.user.uid, emailVerified: result.user.emailVerified });
    
    if (!options?.skipEmailCheck) {
      // Fetch user profile from Firestore
      const userDoc = await getDoc(doc(db, "users", result.user.uid))
      const userData = userDoc.exists() ? userDoc.data() : null
      const isAdmin = userData && (userData.role === "admin" || isAdminUser(result.user.email || ""))
      
      console.log('🔍 User profile check:', {
        userData,
        userRole: userData?.role,
        isAdminByRole: userData?.role === "admin",
        isAdminByEmail: isAdminUser(result.user.email || ""),
        finalIsAdmin: isAdmin,
        emailVerified: result.user.emailVerified
      });
      
      if (!isAdmin && !result.user.emailVerified) {
        console.log('🔍 Blocking unverified non-admin user');
        throw new Error("Please verify your email before logging in.")
      }
    }
    return result.user
  } catch (error) {
    console.error('🔍 Email/password sign in error:', error);
    const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred"
    throw new Error(errorMessage)
  }
}

export const createUserWithEmailAndPassword = async (email: string, password: string, displayName: string) => {
  try {
    const result = await firebaseCreateUserWithEmailAndPassword(auth, email, password)
    const user = result.user

    // Update profile with display name
    await updateProfile(user, { displayName })

    // Send email verification
    await sendEmailVerification(user)

    // Create user document in Firestore
    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      email: user.email,
      displayName,
      name: displayName,
      role: "user", // Default role
      createdAt: new Date().toISOString(),
    })

    return user
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred"
    throw new Error(errorMessage)
  }
}

export const updateUserProfile = async (user: User, displayName: string) => {
  try {
    // Update Firebase Auth profile
    await updateProfile(user, { displayName })
    
    // Update Firestore user document
    await setDoc(doc(db, "users", user.uid), {
      displayName,
      name: displayName,
    }, { merge: true })

    return user
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred"
    throw new Error(errorMessage)
  }
}

export const fetchUserProfile = async (uid: string) => {
  const userDoc = await getDoc(doc(db, "users", uid));
  if (userDoc.exists()) {
    return userDoc.data();
  }
  return null;
};